/** Small presentational building blocks used across screens. */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Link } from '../routes/orgNavigation.jsx';
import { useShell } from '../layouts/ShellContext.jsx';
import { orgRelative } from '../layouts/apps.js';
import { AppIcon } from '../layouts/Brand.jsx';
import { Icon } from './Icon.jsx';

import { useTranslation } from 'react-i18next';
/**
 * A card. `subtitle` (or `description`) sits under the title; `style` is
 * applied to the card itself.
 */
export function Card({
  title,
  subtitle,
  description,
  actions,
  footer,
  children,
  className = '',
  bodyClassName = 'card-body',
  style,
}) {
  const sub = subtitle ?? description;
  return (
    <section className={`card ${className}`} style={style}>
      {(title || actions) && (
        <header className="card-header">
          <div className="card-heading">
            {title && <h2 className="card-title">{title}</h2>}
            {sub && <p className="card-subtitle">{sub}</p>}
          </div>
          {actions && <div className="row card-actions">{actions}</div>}
        </header>
      )}
      <div className={bodyClassName}>{children}</div>
      {footer && <div className="card-footer">{footer}</div>}
    </section>
  );
}

const textOf = (node) => (typeof node === 'string' || typeof node === 'number' ? String(node) : null);

/**
 * The trail above a title, led by the app the page belongs to — so every
 * screen says where it is ("Procurement / Purchase orders / PO-0042") without
 * each one having to. The app is not repeated when the page's own trail
 * already starts with it, or when this IS the app's home.
 */
function useTrail(crumbs) {
  const shell = useShell();
  const location = useLocation();
  const app = shell?.currentApp;
  const list = Array.isArray(crumbs) ? crumbs.filter(Boolean) : [];
  if (!app) return list;

  const { pathname } = orgRelative(location);
  const home = String(app.homeTo ?? app.home).split('?')[0];
  if (pathname === home) return list;

  const first = list[0];
  const leadsWithApp =
    first &&
    ((first.to && String(first.to).split('?')[0] === home) ||
      (textOf(first.label) ?? '').toLowerCase() === app.name.toLowerCase());
  return leadsWithApp ? list : [{ label: app.name, to: app.homeTo ?? app.home }, ...list];
}

export function PageHeader({ title, subtitle, description, breadcrumb, breadcrumbs, badge, actions }) {
  const { t } = useTranslation();
  const shell = useShell();
  const location = useLocation();
  const sub = subtitle ?? description;
  const trail = useTrail(breadcrumb ?? breadcrumbs);

  // Tell the shell what this page is called: the tab title and "Jump back in"
  // then name the record ("PO-0042"), not the list it was opened from.
  const titleText = textOf(title);
  const setPageTitle = shell?.setPageTitle;
  useEffect(() => {
    if (setPageTitle && titleText) setPageTitle(titleText);
  }, [setPageTitle, titleText]);

  const { pathname } = orgRelative(location);
  const pinned = shell?.prefs?.isPinned(pathname) ?? false;
  const canPin = Boolean(shell?.prefs && titleText && pathname !== '/');

  // On an app's own home, the header becomes that app's front door: its tile
  // and its hue, as every app home has.
  const app = shell?.currentApp;
  const atAppHome = Boolean(app) && [app.home, app.homeTo].includes(pathname);

  return (
    <header className={`page-header ${atAppHome ? 'page-header-app' : ''}`} data-app={atAppHome ? app.key : undefined}>
      {atAppHome && <AppIcon app={app} size="xl" />}
      <div className="page-header-main">
        {trail.length > 0 && (
          <nav className="breadcrumb" aria-label={t('a11y.breadcrumb')}>
            {trail.map((crumb, index) => {
              const last = index === trail.length - 1;
              return (
                <span key={`${textOf(crumb.label) ?? index}-${index}`} className="breadcrumb-item">
                  {index > 0 && (
                    <span className="breadcrumb-sep" aria-hidden="true">
                      <Icon name="chevron-right" size={12} strokeWidth={2} />
                    </span>
                  )}
                  {crumb.to ? (
                    <Link to={crumb.to}>{crumb.label}</Link>
                  ) : (
                    <span className="breadcrumb-current" aria-current={last ? 'page' : undefined}>
                      {crumb.label}
                    </span>
                  )}
                </span>
              );
            })}
          </nav>
        )}
        <div className="page-title-row">
          <h1 className="page-title">{title}</h1>
          {badge}
          {canPin && (
            <button
              type="button"
              className="page-pin"
              aria-pressed={pinned}
              aria-label={pinned ? t('actions.unpin') : t('actions.pin')}
              title={pinned ? t('actions.unpinHint') : t('actions.pinHint')}
              onClick={() =>
                shell.prefs.togglePinned({ to: pathname, label: titleText, app: shell.currentApp?.key ?? null })
              }
            >
              <Icon name="star" size={15} />
            </button>
          )}
        </div>
        {sub && <p className="page-subtitle">{sub}</p>}
      </div>
      {actions && <div className="page-actions">{actions}</div>}
    </header>
  );
}

/** A label/value pair on a detail screen. Supports both children and `value` prop. */
export function Detail({ label, children, value, mono }) {
  const content = children !== undefined ? children : value;
  const isEmpty = content === undefined || content === null || content === '';

  return (
    <div className="detail-item">
      <span className="detail-label">{label}</span>
      <span className={`detail-value ${mono ? 'mono' : ''}`}>
        {!isEmpty ? content : <span className="subtle">—</span>}
      </span>
    </div>
  );
}

export function DetailGrid({ children }) {
  return <div className="detail-grid">{children}</div>;
}

/**
 * A dashboard tile. Becomes a link when `to` is given.
 *
 * `tone` marks a figure that wants attention (warning, danger …) with a
 * coloured marker beside the label — the number itself stays in the text
 * colour, so the tone is never carried by colour alone. `hint` is another
 * name for `meta`; `trend` is a short change note ("+12% vs last month").
 */
export function Stat({ label, value, meta, hint, trend, to, tone }) {
  const note = meta ?? hint;
  const body = (
    <>
      <span className="stat-label">{label}</span>
      {tone && <span className={`stat-tone stat-tone-${tone}`} aria-hidden="true" />}
      <span className="stat-value">{value}</span>
      {trend && <span className="stat-trend">{trend}</span>}
      {note && <span className="stat-meta">{note}</span>}
    </>
  );

  if (to) {
    return (
      <Link to={to} className="stat stat-link">
        {body}
        <span className="stat-go" aria-hidden="true">
          <Icon name="arrow-up-right" size={15} />
        </span>
      </Link>
    );
  }

  return <div className="stat">{body}</div>;
}

export function Avatar({ name, email, large }) {
  const source = name || email || '?';
  const parts = String(source).trim().split(/\s+/).filter(Boolean);
  const text = parts.length > 1 ? `${parts[0][0]}${parts.at(-1)[0]}` : String(source).slice(0, 2);

  return (
    <span className={`avatar ${large ? 'avatar-lg' : ''}`} aria-hidden="true">
      {text.toUpperCase()}
    </span>
  );
}

/**
 * A horizontal bar row for a dashboard, drawn without a chart library.
 *
 * Bars of one list are one series, so they share one hue (series slot 1) and
 * length carries the value; `tone` picks a status colour instead, for a bar
 * that MEANS good or bad. The value is always printed beside the bar.
 */
export function BarRow({ label, value, max, formatted, hint, tone }) {
  const pct = max > 0 ? Math.round((Number(value) / Number(max)) * 100) : 0;
  const fill = tone && tone !== 'accent' ? `var(--c-${tone})` : undefined;

  let displayVal = formatted ?? hint;
  if (displayVal === undefined || displayVal === null) {
    if (typeof value === 'number') {
      displayVal = Number.isInteger(value)
        ? value.toLocaleString()
        : Number(value.toFixed(2)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    } else {
      displayVal = value;
    }
  }

  return (
    <div className="bar-row">
      <span className="truncate" title={label}>
        {label}
      </span>
      <span className="bar-track">
        <span className="bar-fill" style={{ width: `${Math.max(pct, value > 0 ? 2 : 0)}%`, background: fill }} />
      </span>
      <span className="num strong">{displayVal}</span>
    </div>
  );
}
